/**
 * Feature flags as plain data — deliberately zod-free.
 *
 * These constants are read on every app boot: the web console's branding store
 * seeds itself from `FEATURE_DEFAULTS` before the first render, and the sidebar
 * gates its nav items on `FeatureKey`. When they lived in `settings.schema.ts`
 * that boot path had to import a zod module, which pulled zod (~146 KB of
 * source) and the whole 46 KB settings schema into the entry chunk — for one
 * object of booleans that needs neither.
 *
 * `settings.schema.ts` re-exports everything here, so `FeatureFlagsSchema` and
 * friends stay the validation source of truth and no import site had to change.
 * A compile-time assertion there keeps the schema and `FeatureFlags` in step.
 */

// ---------------------------------------------------------------------------
// Feature flags — toggle whole modules on/off so one codebase serves both a
// lean "workers attendance" deployment and the full HR suite. Core attendance
// (check-in/out, workers, sites & camps, live + manpower boards, notifications,
// profile, settings, system) is always on and has no flag. Every flag DEFAULTS
// TRUE so existing installs keep all features until an admin turns one off.
// ---------------------------------------------------------------------------

export const FEATURE_KEYS = [
  'leave',
  'corrections',
  'announcements',
  'letters',
  'payroll',
  'reports',
  'shifts',
  'team',
  'orgStructure',
  'documents',
  'faceRecognition',
  'gratuity',
  'lifecycle',
  'dailyRoster',
  'selfService',
  'workers',
  'camps',
  /* A camp tablet's admitted punch moves the worker to that camp
     (campAutoTransfer.ts). Under `camps`: with the pillar off it does nothing. */
  'autoCampTransfer',
  'hardwareTerminals',
  /* ── THE FOUR ADDED 2026-09-19, each for a nav row that no setting could
     reach. Every one defaults TRUE and is present in every preset, because a
     key that is absent from a stored settings blob parses to its default and a
     key that is absent from a PRESET parses to `undefined` — which is not
     `false` and reads as ON wherever a gate fails open.

     A NEW KEY RATHER THAN AN EXISTING ONE, deliberately, in all four cases.
     Gating `/branches` on `orgStructure` or `/settings/devices` on `workers`
     would have cost no new key and would have hidden a screen from any tenant
     who has that flag off for an unrelated reason — invisible until somebody
     goes looking for a page that used to be there. A new key cannot do that:
     no stored blob has it, so every existing tenant reads `true`. */
  'erp',
  'labourSuppliers',
  'legalEntities',
  'deviceApprovals',
  /* Staff tasks: an admin hands work to employees, who tick it off under My
     tasks and share one versioned file back. docs/plans/staff-tasks.md. */
  'tasks',
  /* Morning dispatch: which worker goes to which site today. THE ONE KEY THAT
     DEFAULTS OFF — see `UNRELEASED_FEATURES` below. */
  'dispatch',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/**
 * The full flag set. Structurally identical to `z.infer<typeof
 * FeatureFlagsSchema>` — `settings.schema.ts` asserts that at compile time, so
 * adding a flag to one and not the other fails typecheck rather than drifting.
 */
export type FeatureFlags = Record<FeatureKey, boolean>;

/**
 * PREVIEW — deployed, but shown only to the Super Administrator.
 *
 * A feature listed here is invisible to everyone else WHATEVER its flag says:
 * the web hides its screens and rail rows, the API answers FEATURE_DISABLED,
 * its notifications reach nobody else, and it cannot be assigned to anyone
 * else. That is how a branch that is not launched yet can run on a real
 * server: the owner tries it there, nobody else ever meets it.
 *
 * It matters because every flag DEFAULTS ON for a company that never stored
 * it (settings.features.schema.ts), so without this list a deploy would show
 * the feature to everybody on the day it lands.
 *
 * LAUNCHING = deleting the key from this list (and the feature's flag decides
 * from then on). Staff tasks launched this way on 2026-10-08, as opt-in. The audience is whoever holds `settings.roles.manage` — a
 * platform permission only the built-in Super Administrator role carries.
 */
export const PREVIEW_FEATURES: readonly FeatureKey[] = [];
export const PREVIEW_AUDIENCE_PERMISSION = 'settings.roles.manage';

/** The flags as THIS reader sees them: preview features are off unless they are in the audience. */
export function featuresFor(flags: FeatureFlags, inPreviewAudience: boolean): FeatureFlags {
  if (inPreviewAudience || PREVIEW_FEATURES.length === 0) return flags;
  const out = { ...flags };
  for (const key of PREVIEW_FEATURES) out[key] = false;
  return out;
}

/**
 * Modules that are built but not yet released, so they default OFF.
 *
 * The "every key defaults true" rule exists so a new key cannot REMOVE a screen
 * a tenant already has. A module nobody has ever seen is the opposite case: a
 * true default would ADD it to every deployment on deploy day, before it is
 * released. Off is the default that changes nothing. Releasing the module is
 * deleting its key from this list (and flipping its schema default).
 */
export const UNRELEASED_FEATURES: readonly FeatureKey[] = ['dispatch'];

/**
 * Modules that are RELEASED but OPT-IN: off until a company's administrator
 * switches them on in Settings → Modules. Same default as an unreleased one,
 * for the same reason — a true default would add the module to every company
 * on deploy day — but the toggle is offered and documented as ready.
 *
 * Staff tasks is one: it hands work to employees and notifies them, so a
 * company turns it on when it is ready to use it, not when the server updates.
 */
export const OPT_IN_FEATURES: readonly FeatureKey[] = ['tasks'];

/** Every key whose default is OFF: the unreleased ones and the opt-in ones. */
export const DEFAULT_OFF_FEATURES: readonly FeatureKey[] = [...UNRELEASED_FEATURES, ...OPT_IN_FEATURES];

/**
 * Every flag on except the default-off ones (unreleased or opt-in). Derived from `FEATURE_KEYS` rather than written out, so a new
 * flag cannot be forgotten here.
 *
 * This used to be `FeatureFlagsSchema.parse({})`, which ran a zod parse during
 * module evaluation on every page load to rediscover a constant.
 */
export const FEATURE_DEFAULTS: FeatureFlags = Object.fromEntries(
  FEATURE_KEYS.map((key) => [key, !DEFAULT_OFF_FEATURES.includes(key)])
) as FeatureFlags;

/**
 * The "Full HR suite" preset: every RELEASED module on, opt-in ones included.
 *
 * It was `FEATURE_DEFAULTS`, which is what a company that never chose gets —
 * and that turns the opt-in modules OFF. So a company running Tasks that
 * clicked "Full HR suite" had Tasks switched off by the button named for
 * everything. Only the unreleased stay off here: a preset must not ship a
 * screen that is not finished.
 */
export const FULL_SUITE_FEATURES: FeatureFlags = Object.fromEntries(
  FEATURE_KEYS.map((key) => [key, !UNRELEASED_FEATURES.includes(key)])
) as FeatureFlags;

/**
 * Lean "workers attendance" preset — keeps the attendance core (check-in/out,
 * corrections, reports/boards, team visibility, face enrolment) and turns the
 * HR-heavy modules off. Surfaced as a one-click preset in Settings → Features.
 */
export const LEAN_ATTENDANCE_FEATURES: FeatureFlags = {
  leave: false,
  corrections: true,
  announcements: false,
  letters: false,
  payroll: false,
  reports: true,
  shifts: false,
  team: true,
  orgStructure: false,
  documents: false,
  faceRecognition: true,
  gratuity: false,
  lifecycle: false,
  dailyRoster: true,
  selfService: true,
  workers: true,
  // The lean preset is the SITE-attendance deployment, but it must not silently
  // switch a pillar off: an existing tenant that applies the preset keeps its
  // camps. Turning camps off is a deliberate, separate decision (it hides camp
  // history, though it never changes a recorded figure).
  camps: true,
  autoCampTransfer: true,
  // Standalone ZKTeco/Hikvision face terminals are attendance-CAPTURE
  // infrastructure, same bucket as faceRecognition above — not an HR add-on
  // the lean deployment would want stripped.
  hardwareTerminals: true,
  /* The construction deployment: an ERP it exchanges master data with, labour
     supplied by other companies, more than one legal entity to bill to, and a
     fleet of field tablets. All four are what this preset IS. */
  erp: true,
  labourSuppliers: true,
  legalEntities: true,
  deviceApprovals: true,
  /* Off with the rest of the HR suite this preset turns off: a lean
     attendance tenant hands work out on site, not through the console. */
  tasks: false,
  dispatch: false,
};

/**
 * Office / HR-only preset — the mirror image of `LEAN_ATTENDANCE_FEATURES`.
 * For a tenant with no site workers at all: everyone signs in, punches from
 * the web or their own phone, and HR runs the full suite around them. Turns
 * off the whole site-workforce pillar (`workers`) and everything that exists
 * only to serve it (`camps`, `dailyRoster`, `faceRecognition`) rather than
 * leaving them on and merely hidden — `workers=false` already hides every
 * one of these from the nav and soft-hides their data (never deletes it), so
 * switching back on later loses nothing.
 *
 * `hardwareTerminals` defaults off too: a standalone ZKTeco/Hikvision
 * terminal is capture infrastructure for a physical gate or yard, which an
 * office-only deployment does not have by definition. An admin who does run
 * a badge terminal at a single office door can switch it back on — this is
 * a starting point, not a lock.
 */
export const OFFICE_ONLY_FEATURES: FeatureFlags = {
  leave: true,
  corrections: true,
  announcements: true,
  letters: true,
  payroll: true,
  reports: true,
  shifts: true,
  team: true,
  orgStructure: true,
  documents: true,
  faceRecognition: false,
  gratuity: true,
  lifecycle: true,
  dailyRoster: false,
  selfService: true,
  workers: false,
  camps: false,
  autoCampTransfer: false,
  hardwareTerminals: false,
  /* Runs its own people out of its own HR module. No ERP exchange, nobody
     supplied, and no capture hardware at all — this tenant's attendance comes
     from people signing in. Branches stay: an office company with two offices
     in two emirates is two legal entities, which is a payroll fact. */
  erp: false,
  labourSuppliers: false,
  legalEntities: true,
  deviceApprovals: false,
  /* An office runs its work through people who sign in — exactly who tasks
     are for. */
  tasks: true,
  dispatch: false,
};

/**
 * Terminal-only preset — a gate, a clock, and nothing else.
 *
 * For the deployment that has one job: a hardware terminal at the door records
 * who came in and who left, and somebody reads the log. Nobody signs into
 * Hadir except the administrator. There is no leave to request, no payslip to
 * issue, no manager to approve anything, and no field app.
 *
 * Neither existing preset fits it. `LEAN_ATTENDANCE_FEATURES` is the
 * construction deployment — camps, daily roster, self-service, corrections.
 * `OFFICE_ONLY_FEATURES` turns the entire HR suite ON, which is the opposite of
 * what this tenant asked for, and turns hardware terminals OFF.
 *
 * ── WHY `workers` STAYS ON, WHEN THE TENANT SAYS THEY HAVE NONE ─────────────
 * The flag is named for the site-workforce pillar, but two things this
 * deployment cannot do without live inside it:
 *
 *   LOGIN-LESS PEOPLE. `workers: false` removes the Labour employee type, and
 *     Labour is Hadir's shape for a person with no user account. This tenant's
 *     entire workforce is that shape — the terminal recognises them, they never
 *     sign in.
 *   A PLACE FOR THE TERMINAL. Every terminal is registered against a worksite
 *     (`registerTerminal` requires one), and the Worksites screen is gated on
 *     this flag. With it off, the one screen needed to finish setup is missing
 *     from the navigation.
 *
 * What the tenant means by "no workers" — no camps, no daily roster, no field
 * app, no site-labour machinery — is switched off individually below. The
 * pillar itself is load-bearing here.
 *
 * ── `corrections` IS OFF, AND WHAT THAT DOES *NOT* COST ─────────────────────
 * That module is the REQUEST-and-approve queue, and `POST /api/corrections`
 * raises against `requireCallerEmployeeId` — the CALLER's own record. With
 * nobody signing in there is nobody to raise one, and an administrator cannot
 * use it on somebody else's behalf anyway. It would be a permanently empty
 * screen, so it is off.
 *
 * Reviewing a FLAGGED scan is unaffected, and that is the case which will
 * actually come up: `PATCH /attendance/events/:id/status` is core attendance,
 * carries no feature gate, and is surfaced on the employee's detail page.
 *
 * ── A GAP THIS PRESET INHERITS, STATED RATHER THAN HIDDEN ───────────────────
 * There is no screen anywhere in the console that CREATES a missing punch for
 * another employee. `POST /api/attendance/manual` exists and nothing calls it.
 * That is not caused by this preset — no combination of flags produces one —
 * but a terminal-only deployment feels it hardest, because somebody who walks
 * past the gate without scanning has no second capture path: no app, no
 * self-service, no field device. Until that screen exists such a day is fixed
 * through the API, or not at all.
 *
 * `faceRecognition` is off because it is HADIR's own face system, used by the
 * web punch and the field app. The terminal matches faces on the device, in
 * its own proprietary format, and never consults it.
 */
export const TERMINAL_ONLY_FEATURES: FeatureFlags = {
  leave: false,
  // The request/approve queue, and it raises for the CALLER's own record —
  // useless where nobody signs in. See the note above on what it does not cost.
  corrections: false,
  announcements: false,
  letters: false,
  payroll: false,
  // Reading the log IS the product here. Everything else can go; this cannot.
  reports: true,
  shifts: false,
  team: false,
  orgStructure: false,
  documents: false,
  // Hadir's own face system, not the terminal's. See above.
  faceRecognition: false,
  gratuity: false,
  lifecycle: false,
  dailyRoster: false,
  // Nobody signs in.
  selfService: false,
  // Load-bearing despite the name — see above.
  workers: true,
  camps: false,
  autoCampTransfer: false,
  // The entire point.
  hardwareTerminals: true,
  /* A gate, a clock, and nothing else — so no ERP, nobody supplied, one
     entity. The device queue STAYS: the terminal on the wall is registered
     through it, and hiding it removes the one screen this deployment needs to
     finish setting itself up. */
  erp: false,
  labourSuppliers: false,
  legalEntities: false,
  deviceApprovals: true,
  /* A gate and a clock: nobody signs in to be handed work. */
  tasks: false,
  dispatch: false,
};

/**
 * Panel-only preset — a face at an office door, and the log it writes.
 *
 * The tenant: two wall-mounted Android panels running the Field APK in kiosk
 * mode, one per office door. An employee walks up, is recognised, and an IN or
 * an OUT is recorded. Only administrators sign in. No camps, no payroll, no
 * leave, no letters, no ERP, no labour suppliers, no hardware terminals.
 *
 * ── WHY THIS IS NOT ONE OF THE FOUR ALREADY HERE ────────────────────────────
 *
 * It is `TERMINAL_ONLY_FEATURES` with the door changed, and only two flags
 * separate them — which is the argument FOR a fifth constant rather than
 * against it. A deployment configured by hand from a neighbouring preset is a
 * deployment nobody can audit later: "is `corrections` off because we decided,
 * or because somebody missed it?" A named shape answers that, and
 * `featurePresets.test.ts` then holds it to the whole key set.
 *
 *   `faceRecognition` is ON here and off there. The enrolment endpoints are
 *     gated on it (`field.routes.ts`, `requireFeature`), and a panel that
 *     cannot enrol a face cannot recognise one.
 *   `hardwareTerminals` is OFF here and on there. The door is the Field APK,
 *     not a ZKTeco or Hikvision box pushing over HTTP.
 *
 * Do NOT reach for `OFFICE_ONLY_FEATURES` because of its name. It is the
 * opposite tenant: the entire HR suite ON, `workers` off, `faceRecognition`
 * off, `selfService` on — an office that runs its PEOPLE through Hadir, not
 * one that runs a door.
 *
 * ── WHY `workers` IS ON FOR A TENANT WITH NO SITE WORKERS ───────────────────
 *
 * The same two reasons `TERMINAL_ONLY_FEATURES` gives, and they are load-bearing
 * rather than cosmetic:
 *
 *   LOGIN-LESS PEOPLE. `workers: false` removes the Labour employee type, and
 *     Labour is Hadir's shape for a person with no user account
 *     (`employee.service.ts` refuses a login-less employee without it). This
 *     tenant's entire workforce is that shape.
 *   A PLACE FOR THE PANEL. The Worksites screen is gated on this flag, and
 *     every device is registered against a worksite. With it off, the one
 *     screen needed to finish setup is missing.
 *
 * The flag is named for the site-workforce pillar; what it actually gates is
 * wider than its name, and turning it off to mean "we have no labourers"
 * breaks the deployment.
 */
export const PANEL_ONLY_FEATURES: FeatureFlags = {
  ...TERMINAL_ONLY_FEATURES,
  // The panel IS Hadir's own face system, unlike a terminal's onboard matcher.
  faceRecognition: true,
  // The door is an APK, not a box on the wall speaking ADMS or ISAPI.
  hardwareTerminals: false,
};

/**
 * A SCHEDULED REPORT WHOSE DATA BELONGS TO A MODULE. While that module is off
 * the schedule is PAUSED: the job skips it and the editor says so (owner,
 * 2026-10-21) — a Documents or Leave switch that says its screens and warnings
 * go must not keep emailing the same data. Kept, not deleted: turning the
 * module back on resumes it unchanged. Keyed by `ReportType` (settings schema).
 */
export const REPORT_TYPE_MODULE: Readonly<Record<string, FeatureKey>> = {
  'document-expiry': 'documents',
  'leave-balance': 'leave',
};

/** The module pausing this report type, or null while it may run. */
export function reportPausedBy(
  reportType: string,
  features: Partial<Record<FeatureKey, boolean>> | null | undefined
): FeatureKey | null {
  const owner = REPORT_TYPE_MODULE[reportType];
  return owner && features?.[owner] === false ? owner : null;
}
